import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  TextField,
  Button,
  Card,
  CardMedia,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
} from "@mui/material";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";

const DialogCrearPresentacion = ({
  open,
  onClose,
  creating,
  createResult,
  selectedIds,
  results,
  API_URL,
  theme,
  // vistas delante/detrás
  itemViews,
  onChangeItemView,
  // carpetas
  folders,
  selectedFolder,
  onChangeFolder,
  newFolderName,
  onChangeNewFolderName,
  creatingFolder,
  onCreateFolder,
  loadingFolders,
  // avatar/modelo
  avatares,
  selectedAvatarPath,
  onChangeAvatarPath,
  onAvatarFile,
  loadingAvatares,
  uploadingAvatar,
  // plantillas
  plantillasRegistry,
  plantillasSubidas,
  selectedPlantilla,
  onChangePlantilla,
  onPlantillaFile,
  loadingPlantillas,
  uploadingPlantilla,
  // marca
  marca,
  onChangeMarca,
  // motor
  engine,
  onChangeEngine,
  // prompt extra
  extraPrompt,
  onChangeExtraPrompt,
  // confirmación
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);

  const faltanVistasConAvatar =
    !!selectedAvatarPath &&
    results.some(
      (item) =>
        selectedIds.has(item.id) &&
        itemViews[item.id] !== "front" &&
        itemViews[item.id] !== "back",
    );

  const selectedTemplateFromRegistry = useMemo(
    () => plantillasRegistry.find((t) => t.id === selectedPlantilla) || null,
    [plantillasRegistry, selectedPlantilla],
  );

  const canPreview =
    !!selectedAvatarPath &&
    !!selectedTemplateFromRegistry &&
    selectedIds.size > 0;

  const previewElement = useMemo(() => {
    if (!canPreview || !selectedTemplateFromRegistry) return null;
    const cfg = selectedTemplateFromRegistry.config || {};
    const regions = cfg.regions || [];
    if (!regions.length) return null;
    const textFields = cfg.textFields || [];
    const marcaField = textFields.find((t) => t.id === "marca");

    // Avatar front/back
    const selectedAvatar = avatares.find(
      (av) => av.path === selectedAvatarPath,
    );
    let avatarFrontUrl = null;
    let avatarBackUrl = null;
    if (selectedAvatar) {
      const baseMatch = selectedAvatar.filename.match(
        /^(.*)_(Front|Back)(\.[^.]+)$/i,
      );
      if (baseMatch) {
        const base = baseMatch[1];
        const ext = baseMatch[3];
        const frontName = `${base}_Front${ext}`;
        const backName = `${base}_Back${ext}`;
        const frontAvatar = avatares.find((av) => av.filename === frontName);
        const backAvatar = avatares.find((av) => av.filename === backName);
        const frontSrc = (frontAvatar || selectedAvatar).filename;
        const backSrc = (backAvatar || frontAvatar || selectedAvatar).filename;
        avatarFrontUrl = `${API_URL}/api/nas-matrices/AVATARES/${frontSrc}`;
        avatarBackUrl = `${API_URL}/api/nas-matrices/AVATARES/${backSrc}`;
      } else {
        const src = selectedAvatar.filename;
        avatarFrontUrl = `${API_URL}/api/nas-matrices/AVATARES/${src}`;
        avatarBackUrl = avatarFrontUrl;
      }
    }

    // Imágenes originales de prendas seleccionadas
    const garmentUrls = results
      .filter((item) => selectedIds.has(item.id))
      .map((item) => {
        const path = item.path || item.payload?.path;
        if (!path) return null;
        return (
          `${API_URL}/api/nas-presentaciones` +
          path.replace("/mnt/nas/WFT2/FOTOS_WEB", "")
        );
      })
      .filter(Boolean);

    const regionImages = [];
    if (avatarFrontUrl) regionImages.push(avatarFrontUrl);
    if (avatarBackUrl) regionImages.push(avatarBackUrl);
    regionImages.push(...garmentUrls);
    if (!regionImages.length) return null;

    const width = cfg.width || 2480;
    const height = cfg.height || 3508;

    return (
      <Box
        sx={{
          mt: 1.5,
          p: 1,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          {t("presentacionesDialog.previewApprox")}
        </Typography>
        {selectedAvatarPath && garmentUrls.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            {t("presentacionesDialog.previewOverlayHint")}
          </Typography>
        )}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: `${width}/${height}`,
            backgroundColor: "#ffffff",
          }}
        >
          {marcaField && (
            <Box
              sx={{
                position: "absolute",
                left: `${(marcaField.x || 0) * 100}%`,
                top: `${(marcaField.y || 0) * 100}%`,
                width: `${(marcaField.width || 0.35) * 100}%`,
                height: `${(marcaField.height || 0.06) * 100}%`,
                bgcolor: "grey.800",
                display: "flex",
                alignItems: "center",
                px: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#fff", fontWeight: "bold" }}
              >
                {marca || t("presentacionesDialog.defaults.brand")}
              </Typography>
            </Box>
          )}
          {regions.map((r, idx) => {
            const img = regionImages[idx % regionImages.length];
            if (!img) return null;
            const regionId = String(r.id || "").toLowerCase();
            const isModelRegion =
              regionId.includes("front") ||
              regionId.includes("back") ||
              regionId.includes("model") ||
              regionId.includes("hero");
            const avatarForRegion = regionId.includes("back")
              ? avatarBackUrl
              : avatarFrontUrl;
            const overlayGarment = garmentUrls[idx % garmentUrls.length];

            if (isModelRegion && avatarForRegion && overlayGarment) {
              return (
                <Box
                  key={`${r.id || idx}`}
                  sx={{
                    position: "absolute",
                    left: `${(r.x || 0) * 100}%`,
                    top: `${(r.y || 0) * 100}%`,
                    width: `${(r.width || 0.3) * 100}%`,
                    height: `${(r.height || 0.4) * 100}%`,
                    overflow: "hidden",
                    borderRadius: 0.5,
                    border: "1px solid rgba(0,0,0,0.08)",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <Box
                    component="img"
                    src={avatarForRegion}
                    alt={`${r.id || `region-${idx}`}-avatar`}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <Box
                    component="img"
                    src={overlayGarment}
                    alt={r.id || `region-${idx}`}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      opacity: 0.82,
                      mixBlendMode: "multiply",
                      filter: "contrast(1.08) saturate(1.06)",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              );
            }
            return (
              <Box
                key={`${r.id || idx}`}
                component="img"
                src={img}
                alt={r.id || `region-${idx}`}
                sx={{
                  position: "absolute",
                  left: `${(r.x || 0) * 100}%`,
                  top: `${(r.y || 0) * 100}%`,
                  width: `${(r.width || 0.3) * 100}%`,
                  height: `${(r.height || 0.4) * 100}%`,
                  objectFit: "contain",
                }}
              />
            );
          })}
        </Box>
      </Box>
    );
  }, [
    API_URL,
    avatares,
    canPreview,
    marca,
    results,
    selectedAvatarPath,
    selectedIds,
    selectedTemplateFromRegistry,
    t,
    theme,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 8,
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(90deg, rgba(255,179,0,0.28), rgba(255,152,0,0.18))",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "1.1rem",
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 20 }} />
              {t("presentacionesDialog.title")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("presentacionesDialog.engineHelp")}
            </Typography>
          </Box>
          <Chip
            size="small"
            label="PRO FLOW"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box
          sx={{
            mb: 2.5,
            p: 1.25,
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "divider",
            backgroundColor: "rgba(0,0,0,0.02)",
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Chip
            size="small"
            label={`${t("presentacionesDialog.selectedGarments")}: ${selectedIds.size}`}
            color="info"
          />
          {selectedAvatarPath && (
            <Chip
              size="small"
              label={t("presentacionesDialog.generatedOnModel")}
              color="success"
              variant="outlined"
            />
          )}
          {showPreview && (
            <Chip
              size="small"
              label={t("presentacionesDialog.previewWithoutAi")}
              color="warning"
            />
          )}
        </Box>

        {selectedAvatarPath && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 2 }}>
              {t("presentacionesDialog.garmentViewRequired")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              {t("presentacionesDialog.garmentViewHelp")}
            </Typography>
            {faltanVistasConAvatar && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("presentacionesDialog.garmentViewAlert")}
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                mb: 2,
              }}
            >
              {results
                .filter((item) => selectedIds.has(item.id))
                .map((item) => {
                  const path = item.path || item.payload?.path;
                  if (!path) return null;
                  const design = item.design || item.payload?.design;
                  const imageUrl =
                    `${API_URL}/api/nas-presentaciones` +
                    path.replace("/mnt/nas/WFT2/FOTOS_WEB", "");
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        backgroundColor: theme.palette.caja.main,
                        width: "48.5%",
                      }}
                    >
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={design}
                        sx={{
                          width: 56,
                          height: 56,
                          objectFit: "cover",
                          borderRadius: 0.5,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          display="block"
                          noWrap
                          sx={{ maxWidth: 120 }}
                        >
                          {design}
                        </Typography>
                        <FormControl
                          size="small"
                          sx={{ minWidth: 140 }}
                          required
                        >
                          <Select
                            value={itemViews[item.id] || ""}
                            onChange={(e) =>
                              onChangeItemView(item.id, e.target.value)
                            }
                            displayEmpty
                            renderValue={(v) =>
                              v === "back"
                                ? t("presentacionesDialog.viewBack")
                                : v === "front"
                                  ? t("presentacionesDialog.viewFront")
                                  : t("presentacionesDialog.chooseView")
                            }
                            error={
                              !(
                                itemViews[item.id] === "front" ||
                                itemViews[item.id] === "back"
                              )
                            }
                            sx={{
                              backgroundColor: theme.palette.background.paper,
                            }}
                          >
                            <MenuItem value="">
                              <em>
                                {t("presentacionesDialog.chooseFrontBack")}
                              </em>
                            </MenuItem>
                            <MenuItem value="front">
                              {t("presentacionesDialog.viewFront")}
                            </MenuItem>
                            <MenuItem value="back">
                              {t("presentacionesDialog.viewBack")}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </>
        )}

        <Box
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: theme.palette.background.cajaInpunts,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <SettingsSuggestOutlinedIcon color="warning" fontSize="small" />
            <Typography variant="subtitle2">
              {t("presentacionesDialog.engineTitle")}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1.5 }}
          >
            {t("presentacionesDialog.engineHelp")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              sx={{
                fontSize: "0.73rem",
                color: "orange",
                borderColor: "orange",
              }}
              size="small"
              variant={
                !showPreview && engine === "gpt-image"
                  ? "contained"
                  : "outlined"
              }
              onClick={() => {
                setShowPreview(false);
                onChangeEngine("gpt-image");
              }}
            >
              {t("presentacionesSearch.engines.gptName")}
            </Button>
            <Button
              sx={{ fontSize: "0.73rem", color: "red", borderColor: "red" }}
              size="small"
              variant={
                !showPreview && engine === "flux" ? "contained" : "outlined"
              }
              onClick={() => {
                setShowPreview(false);
                onChangeEngine("flux");
              }}
            >
              {t("presentacionesSearch.engines.fluxName")}
            </Button>
            <Button
              sx={{ fontSize: "0.73rem", color: "blue", borderColor: "blue" }}
              size="small"
              variant={
                !showPreview && engine === "nano-banana"
                  ? "contained"
                  : "outlined"
              }
              onClick={() => {
                setShowPreview(false);
                onChangeEngine("nano-banana");
              }}
            >
              {t("presentacionesSearch.engines.nanoName")}
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              sx={{ fontSize: "0.73rem" }}
              size="small"
              variant={showPreview ? "contained" : "outlined"}
              disabled={!canPreview}
              onClick={() => setShowPreview((v) => !v)}
            >
              {t("presentacionesDialog.previewWithoutAi")}
            </Button>
          </Box>
          {showPreview && previewElement}
        </Box>

        <Box
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: theme.palette.background.cajaInpunts,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {t("presentacionesDialog.criticalDetailTitle")}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            {t("presentacionesDialog.criticalDetailHelp")}
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder={t("presentacionesDialog.criticalDetailPlaceholder")}
            value={extraPrompt}
            onChange={(e) => onChangeExtraPrompt(e.target.value)}
            multiline
            minRows={2}
            sx={{ backgroundColor: theme.palette.background.paper }}
          />
        </Box>

        <Box
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: theme.palette.background.cajaInpunts,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <FolderOpenOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">
              {t("presentacionesDialog.folderTitle")}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1.5 }}
          >
            {t("presentacionesDialog.folderHelp")}
          </Typography>
          {!selectedFolder.trim() && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("presentacionesDialog.folderRequired")}
            </Alert>
          )}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
            }}
          >
            <FormControl size="small" sx={{ width: "49%" }} required>
              <InputLabel id="create-folder-label">
                {t("presentacionesDialog.folderLabel")}
              </InputLabel>
              <Select
                labelId="create-folder-label"
                value={selectedFolder}
                label={t("presentacionesDialog.folderLabel")}
                onChange={(e) => onChangeFolder(e.target.value)}
                disabled={loadingFolders}
                displayEmpty
                renderValue={(v) => v || t("presentacionesDialog.folderChoose")}
                sx={{ backgroundColor: theme.palette.background.paper }}
              >
                <MenuItem value="">
                  <em>{t("presentacionesDialog.folderChoose")}</em>
                </MenuItem>
                {folders.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder={t("presentacionesDialog.newFolderName")}
              value={newFolderName}
              onChange={(e) => onChangeNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onCreateFolder();
                }
              }}
              sx={{
                width: "49%",
                backgroundColor: theme.palette.background.paper,
              }}
            />
            <Button
              /* size="small" */
              sx={{ width: "100%" }}
              variant="outlined"
              startIcon={<CreateNewFolderOutlinedIcon />}
              onClick={onCreateFolder}
              disabled={creatingFolder || !newFolderName.trim()}
            >
              {creatingFolder
                ? t("presentacionesDialog.creatingFolder")
                : t("presentacionesDialog.createFolder")}
            </Button>
          </Box>
          {selectedFolder.trim() && (
            <Typography variant="body2" color="primary.main" sx={{ mt: 1.5 }}>
              {t("presentacionesDialog.saveIn")}:{" "}
              <strong>{selectedFolder}</strong>
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: theme.palette.background.cajaInpunts,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <PersonOutlineOutlinedIcon color="secondary" fontSize="small" />
            <Typography variant="subtitle2">
              {t("presentacionesDialog.modelTitle")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Button
                sx={{ width: "100%" }}
                variant={selectedAvatarPath ? "outlined" : "contained"}
                onClick={() => onChangeAvatarPath(null)}
              >
                {t("presentacionesDialog.none")}
              </Button>
              <Button
                sx={{ width: "100%" }}
                variant="outlined"
                component="label"
                disabled={uploadingAvatar}
              >
                {uploadingAvatar
                  ? t("presentacionesDialog.uploading")
                  : t("presentacionesDialog.uploadModel")}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={onAvatarFile}
                />
              </Button>
            </Box>
            {loadingAvatares && (
              <Typography variant="caption">
                {t("presentacionesDialog.loading")}
              </Typography>
            )}
            {avatares.map((av) => {
              const isSelected = selectedAvatarPath === av.path;
              const imageUrl = `${API_URL}/api/nas-matrices/AVATARES/${av.filename}`;
              return (
                <Card
                  key={av.path}
                  onClick={() => onChangeAvatarPath(av.path)}
                  sx={{
                    width: 72,
                    cursor: "pointer",
                    border: isSelected
                      ? `2px solid ${theme.palette.primary.main}`
                      : "1px solid #ddd",
                  }}
                >
                  <CardMedia
                    component="img"
                    height="80"
                    image={imageUrl}
                    alt={av.filename}
                  />
                </Card>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            mb: 1.5,
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: theme.palette.background.cajaInpunts,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <DashboardCustomizeOutlinedIcon color="action" fontSize="small" />
            <Typography variant="subtitle2">
              {t("presentacionesDialog.templateTitle")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Button
                sx={{ width: "100%" }}
                variant={selectedPlantilla ? "outlined" : "contained"}
                onClick={() => onChangePlantilla(null)}
              >
                {t("presentacionesDialog.none")}
              </Button>
              <Button
                sx={{ width: "100%" }}
                variant="outlined"
                component="label"
                disabled={uploadingPlantilla}
              >
                {uploadingPlantilla
                  ? t("presentacionesDialog.uploading")
                  : t("presentacionesDialog.uploadTemplate")}
                <input
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  hidden
                  onChange={onPlantillaFile}
                />
              </Button>
            </Box>
            {loadingPlantillas && (
              <Typography variant="caption">
                {t("presentacionesDialog.loading")}
              </Typography>
            )}
            {plantillasRegistry.map((template) => {
              const isSelected = selectedPlantilla === template.id;
              const Preview = template.Preview;
              return (
                <Card
                  key={template.id}
                  onClick={() => onChangePlantilla(template.id)}
                  sx={{
                    width: 72,
                    cursor: "pointer",
                    border: isSelected
                      ? `2px solid ${theme.palette.primary.main}`
                      : "1px solid #ddd",
                    overflow: "hidden",
                  }}
                  title={template.description || template.name}
                >
                  <Box sx={{ width: 72, height: 80 }}>
                    <Preview
                      marca={marca || t("presentacionesDialog.defaults.brand")}
                    />
                  </Box>
                </Card>
              );
            })}
            {plantillasSubidas.map((pl) => {
              const isSelected = selectedPlantilla === pl.filename;
              const imageUrl = `${API_URL}/api/nas-matrices/PLANTILLAS/${encodeURIComponent(pl.filename)}`;
              return (
                <Card
                  key={pl.path}
                  onClick={() => onChangePlantilla(pl.filename)}
                  sx={{
                    width: 72,
                    cursor: "pointer",
                    border: isSelected
                      ? `2px solid ${theme.palette.primary.main}`
                      : "1px solid #ddd",
                  }}
                >
                  <CardMedia
                    component="img"
                    height="80"
                    image={imageUrl}
                    alt={pl.filename}
                  />
                </Card>
              );
            })}
          </Box>
          {selectedPlantilla && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t("presentacionesDialog.brandTitle")}
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder={t("presentacionesDialog.brandPlaceholder")}
                value={marca}
                onChange={(e) => onChangeMarca(e.target.value)}
                /* helperText="Marca a mostrar en la presentación (ej. ZARA KIDS)." */
                sx={{
                  width: "100%",
                  backgroundColor: theme.palette.background.paper,
                  fontSize: "8px",
                }}
              />
            </Box>
          )}
        </Box>

        {creating && (
          <Typography>{t("presentacionesDialog.creating")}</Typography>
        )}
        {createResult && !creating && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {createResult.error
              ? `${t("presentacionesDialog.error")}: ${createResult.error}`
              : t("presentacionesDialog.createdOk")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Button onClick={onClose} disabled={creating} variant="text">
          {t("common.close")}
        </Button>
        {!(createResult && !createResult.error) && (
          <Button
            onClick={onConfirm}
            disabled={
              creating ||
              !selectedFolder.trim() ||
              faltanVistasConAvatar ||
              showPreview
            }
            variant="contained"
            sx={{ minWidth: 170, fontWeight: 700 }}
          >
            {t("presentacionesDialog.confirm")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DialogCrearPresentacion;
